<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3"
import { SbList, SbListItem, SbListLabel, SbListSection } from "@unio/saltbox-ui"
import { computed, ref } from "vue"

import DeleteConfirmModal from "@/components/DeleteConfirmModal.vue"
import FlashAlerts from "@/components/FlashAlerts.vue"
import NarrowPage from "@/components/NarrowPage.vue"
import RoleBadge from "@/components/RoleBadge.vue"
import { useAuthUser } from "@/composables/useAuthUser"
import { useNavbar } from "@/composables/useNavbar"
import { ICON } from "@/lib/icons"
import { accountInvitationsPath, accountMemberPath } from "@/routes"
import type { MemberEntry } from "@/types/models"

const props = defineProps<{
  members: MemberEntry[]
}>()

const { title } = useNavbar({ title: "Members" })
const { isAccountOwner } = useAuthUser()

const deleteTarget = ref<MemberEntry | null>(null)
const deleteOpen = computed(() => deleteTarget.value !== null)
const deleting = ref(false)

function toggleRole(member: MemberEntry) {
  const newRole = member.role === "owner" ? "member" : "owner"
  router.patch(accountMemberPath({ id: member.id }), { role: newRole })
}

function confirmRemove() {
  if (!deleteTarget.value) return
  deleting.value = true
  router.delete(accountMemberPath({ id: deleteTarget.value.id }), {
    onFinish: () => {
      deleting.value = false
      deleteTarget.value = null
    },
  })
}
</script>

<template>
  <Head :title="title" />

  <NarrowPage>
    <FlashAlerts />

    <div v-if="isAccountOwner" class="flex justify-end">
      <UButton
        :icon="ICON.add"
        label="Invite"
        color="neutral"
        variant="subtle"
        size="sm"
        :to="accountInvitationsPath()"
      />
    </div>

    <SbList bordered separator>
      <SbListItem v-for="member in members" :key="member.id">
        <SbListSection avatar>
          <UAvatar
            :src="member.avatar_url ?? undefined"
            :text="member.user_initials"
            :alt="member.user_name"
            size="sm"
          />
        </SbListSection>
        <SbListSection>
          <SbListLabel>
            {{ member.user_name }}
            <span v-if="member.is_current_user" class="text-muted text-xs">(you)</span>
          </SbListLabel>
          <SbListLabel caption>{{ member.user_email }}</SbListLabel>
        </SbListSection>
        <SbListSection side>
          <div class="flex items-center gap-2">
            <RoleBadge :role="member.role" />
            <UDropdownMenu
              v-if="isAccountOwner && !member.is_current_user"
              :items="[
                [
                  {
                    label: member.role === 'owner' ? 'Make member' : 'Make owner',
                    icon: ICON.members,
                    onSelect: () => toggleRole(member),
                  },
                  {
                    label: 'Remove',
                    icon: ICON.delete,
                    color: 'error' as const,
                    onSelect: () => (deleteTarget = member),
                  },
                ],
              ]"
            >
              <UButton :icon="ICON.more" color="neutral" variant="ghost" size="xs" square />
            </UDropdownMenu>
          </div>
        </SbListSection>
      </SbListItem>
    </SbList>

    <DeleteConfirmModal
      :open="deleteOpen"
      :title="`Remove ${deleteTarget?.user_name}?`"
      :loading="deleting"
      action-label="Remove"
      @update:open="!$event && (deleteTarget = null)"
      @confirm="confirmRemove"
    >
      <p class="text-sm">They will lose access to this account's inventory.</p>
    </DeleteConfirmModal>
  </NarrowPage>
</template>
